import { ArrowUpRightIcon } from "@heroicons/react/24/outline"
import { useMemo } from "react"
import { ComposedChart } from "~/ui-rtk/components/ui/charts"
import ChartLoader from "~/ui-rtk/components/ui/charts/ChartLoader"
import { Card } from "~/ui-rtk/components/ui/common"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import {
  AggregationType,
  VISUALIZATIONS_MAP,
  VISUALIZATION_TYPE,
} from "~/ui-rtk/constants/charts"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import useSources from "~/ui-rtk/hooks/sources"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"

export const WIDGET = "brand_value_stacked_chart"

const TOOLTIP_LINKS = [
  {
    label: "See Paid Media",
    link: brandRoutes.BRAND_MEDIA.PAID_OPTIMIZATION,
    color: "#EE8434",
  },
  {
    label: "See Organic Media",
    link: brandRoutes.ORGANIC_MEDIA.ORGANIC_SOCIAL_POSTS,
    color: "#66DECD",
  },
  {
    label: "See Earned Media",
    link: brandRoutes.ORGANIC_MEDIA.MEDIA_MENTIONS,
    color: "#F4D148",
  },
]

export default function BrandValueOverviewChart({
  granularity,
  dateRange,
  compareRange,
  cubeQueryParams,
  selectedMetrics,
  graphs,
}: {
  granularity: AggregationType
  dateRange: [Date, Date]
  compareRange?: [Date, Date]
  cubeQueryParams?: Record<string, string | string[]>
  selectedMetrics: TMultiSelectOption[]
  graphs: Array<{ dataKey: string; label: string; color: string }>
}) {
  const visualization = VISUALIZATIONS_MAP[WIDGET]
  const { sources } = useSources()
  const navigate = useStableNavigate()

  const selectedDataKeys = useMemo(
    () => selectedMetrics.map(({ value }) => value),
    [selectedMetrics],
  )

  const selectedGraphs = useMemo(
    () =>
      graphs.filter(({ dataKey }: { dataKey: string }) =>
        selectedDataKeys.includes(dataKey),
      ),
    [graphs, selectedDataKeys],
  )

  const tooltipFooter = useMemo(
    () => (
      <div className="grid grid-cols-2 gap-system-xs">
        {TOOLTIP_LINKS.map(({ label, color, link }) => (
          <div
            className="flex items-center justify-between cursor-pointer text-sm"
            onClick={() => navigate(link)}
            key={link}
          >
            <span>{label}</span>
            <div
              className="w-6 h-6 flex items-center justify-center"
              style={{
                backgroundColor: `${color}66`,
              }}
            >
              <ArrowUpRightIcon
                className="w-4 h-4"
                style={{
                  color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    ),
    [],
  )

  return (
    <Card
      rounded
      className="bg-system-blue-800 h-full p-system-sm rounded-system-md"
    >
      <ChartLoader
        chartId={WIDGET}
        className="border-0 h-full pb-0"
        widgetType={VISUALIZATION_TYPE.ComposedChart}
        component={ComposedChart}
        dateRange={dateRange}
        compareRange={compareRange ?? undefined}
        key={WIDGET}
        granularity={granularity}
        sources={sources}
        enableCSVExport={true}
        csvExportFilename={WIDGET || "brand-value-overview"}
        enableChartDataExport={true}
        chartDataExportFilename="brand-value-overview-chart-data"
        hideAggSelector={true}
        requiredSources={visualization.requiredSources}
        cubeQueryParams={{
          [WIDGET]: {
            ...cubeQueryParams,
            granularity,
          },
        }}
        description={visualization.description ?? undefined}
        compareMode={
          visualization.props?.comparisonEnabled &&
          (compareRange?.length ?? 0) > 0
        }
        {...visualization.props}
        graphs={selectedGraphs}
        tooltipFooter={tooltipFooter}
      />
    </Card>
  )
}
